/**
 * Zod schemas derived from the ZenStack 3 schema at runtime.
 *
 * Replaces the v2 `./zod` tree (4,339 generated files emitted by the `@core/zod` plugin, which v3
 * removed). Every schema is built on first use and memoised, so binding one at module scope in a
 * component file costs a single build for the process.
 *
 * ⚠️ **Reads `zen3/schema-lite`, never `zen3/schema`.** The lite artifact is the client-safe one and
 * is already in client bundles — every generated hook opens with the same import — so the schemas
 * here add no schema payload. Pointing this at `zen3/schema` would ship policy expressions to the
 * browser.
 *
 * ⚠️ **A DateTime column is typed `z.union([z.date(), z.iso.datetime()])` here** (`@zenstackhq/zod`
 * 3.9.3+), where the v2 tree emitted a bare `z.date()`. `z.iso.datetime()` accepts only a `Z` suffix,
 * not numeric offsets — editor DateTime pickers must emit `Date`, not Luxon `toISO()` strings.
 * `typeOfZodSchema` (`@erikdakoda/utils/zod`) collapses that union back to `'date'`
 * so the data grid's filter branches still fire; without it every date filter silently stops
 * matching. `mediumSchema.unit.test.ts` pins factory equivalence against the full schema.
 *
 * ⚠️ **Every object schema is `.strip()`-ed to match v2.** `createSchemaFactory` returns
 * `z.strictObject`s, which THROW on unknown keys; the v2 `@core/zod` tree emitted plain `z.object`s
 * that silently STRIPPED unknowns. Model-editor forms validate the whole form value — which carries
 * virtual fields (`aiModelDetails`) and relations (`childPrompts`) that are not scalars in these
 * schemas — so strict rejection broke save on every editor. `.strip()` restores v2 semantics while
 * keeping the shape and any `@@validate` refinements intact. Nothing relied on strictness: it was
 * introduced by this migration's factory swap and never shipped working.
 *
 * Return types are explicit — every exported function declares a named `Stripped…Schema` alias
 * instead of inferring — so `tsc -p tsconfig.build.json` can emit this file into `dist-types/`
 * without TS2883/TS7056/TS4094 on the private `factory` class.
 */
import { createSchemaFactory, type GetModelSchemaShapeWithOptions } from '@zenstackhq/zod';
import type { GetEnum, GetEnums, GetModels } from '@zenstackhq/schema';
import type { z } from 'zod';
import { schema } from './zen3/schema-lite';

const factory = createSchemaFactory(schema);

/** Model names accepted by {@link modelSchema}, taken from the lite schema. */
export type ZodModelName = GetModels<typeof schema>;

/** Enum names accepted by {@link enumSchema}. */
export type ZodEnumName = GetEnums<typeof schema>;

type ModelShape<M extends ZodModelName> = GetModelSchemaShapeWithOptions<typeof schema, M, object>;
type UpdateShape<M extends ZodModelName> = GetModelSchemaShapeWithOptions<
  typeof schema,
  M,
  { optionality: 'all' }
>;
type CreateShape<M extends ZodModelName> = GetModelSchemaShapeWithOptions<
  typeof schema,
  M,
  { optionality: 'defaults' }
>;

type StrippedEnumSchema<E extends ZodEnumName> = z.ZodEnum<{
  [Key in keyof GetEnum<typeof schema, E>]: GetEnum<typeof schema, E>[Key];
}>;

type StrippedModelSchema<M extends ZodModelName> = z.ZodObject<ModelShape<M>, z.core.$strip>;
type StrippedUpdateSchema<M extends ZodModelName> = z.ZodObject<UpdateShape<M>, z.core.$strip>;
type StrippedCreateSchema<M extends ZodModelName> = z.ZodObject<CreateShape<M>, z.core.$strip>;

const modelCache = new Map<string, unknown>();
const updateCache = new Map<string, unknown>();
const createCache = new Map<string, unknown>();
const enumCache = new Map<string, unknown>();

function cached<T>(store: Map<string, unknown>, key: string, build: () => T): T {
  const hit = store.get(key);
  if (hit !== undefined) return hit as T;
  const built = build();
  store.set(key, built);
  return built;
}

/**
 * The full model schema — every scalar field, all required. The v2 counterpart was `XSchema`.
 *
 * Relation fields are absent (v2 included them). That is behaviourally identical for the data grid:
 * its filter converter has no `case 'object'`, so relation fields contributed nothing there either.
 * @param model Model name, e.g. `'Bot'`.
 * @returns A memoised Zod object schema for the model.
 */
export function modelSchema<M extends ZodModelName>(model: M): StrippedModelSchema<M> {
  return cached(modelCache, model, () =>
    factory.makeModelSchema(model).strip(),
  ) as unknown as StrippedModelSchema<M>;
}

/**
 * The model schema with every field optional — the v2 `XPrismaUpdateSchema`.
 * @param model Model name, e.g. `'Bot'`.
 * @returns A memoised Zod object schema with all fields optional.
 */
export function modelUpdateSchema<M extends ZodModelName>(model: M): StrippedUpdateSchema<M> {
  return cached(updateCache, model, () =>
    factory.makeModelSchema(model, { optionality: 'all' }).strip(),
  ) as unknown as StrippedUpdateSchema<M>;
}

/**
 * The model schema shaped for creates: fields carrying `@default` or `@updatedAt` are optional,
 * everything else stays required. The v2 counterpart was `XCreateInputObjectSchema`.
 * @param model Model name, e.g. `'Book'`.
 * @returns A memoised Zod object schema suitable for validating create input.
 */
export function modelCreateSchema<M extends ZodModelName>(model: M): StrippedCreateSchema<M> {
  return cached(createCache, model, () =>
    factory.makeModelSchema(model, { optionality: 'defaults' }).strip(),
  ) as unknown as StrippedCreateSchema<M>;
}

/**
 * A schema for one of the schema's enums — the v2 `XSchema` under `zod/enums/`.
 * @param name Enum name, e.g. `'LifeDomainId'`.
 * @returns A memoised Zod enum schema.
 */
export function enumSchema<E extends ZodEnumName>(name: E): StrippedEnumSchema<E> {
  return cached(enumCache, name, () => factory.makeEnumSchema(name)) as StrippedEnumSchema<E>;
}
